import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  addCalendarDaysToIsoDate,
  clampIsoDateToRange,
  getUkPrebookTimeReadyDefaultsOneHourFromNow,
  getUkTodayIsoDate,
  isUkCollectionScheduleInPast,
} from '../../../utils/dateAndTime';

export function useCitysprintSchedule({ initial, enabled }) {
  const todayIso = useMemo(() => getUkTodayIsoDate(), []);
  const collectionDateMaxIso = useMemo(
    () => addCalendarDaysToIsoDate(todayIso, 7),
    [todayIso]
  );

  const [seed] = useState(() => ({
    collectionDate: initial?.collectionDate?.trim() || '',
    timeHour: initial?.timeHour || '',
    timeMinute: initial?.timeMinute || '',
  }));
  const [collectionDate, setCollectionDate] = useState(seed.collectionDate);
  const [timeHour, setTimeHour] = useState(seed.timeHour);
  const [timeMinute, setTimeMinute] = useState(seed.timeMinute);

  const [hasAppliedDefaults, setHasAppliedDefaults] = useState(() =>
    Boolean(seed.collectionDate || seed.timeHour || seed.timeMinute)
  );

  useEffect(() => {
    if (!enabled || hasAppliedDefaults) {
      return;
    }
    const defaults = getUkPrebookTimeReadyDefaultsOneHourFromNow();
    setCollectionDate(todayIso);
    setTimeHour(defaults.hour);
    setTimeMinute(defaults.minute);
    setHasAppliedDefaults(true);
  }, [enabled, hasAppliedDefaults, todayIso]);

  useEffect(() => {
    if (!collectionDate?.trim()) {
      return;
    }
    const next = clampIsoDateToRange(
      collectionDate,
      todayIso,
      collectionDateMaxIso
    );
    if (next !== collectionDate) {
      setCollectionDate(next);
    }
  }, [collectionDate, todayIso, collectionDateMaxIso]);

  const scheduleComplete =
    Boolean(collectionDate?.trim()) && Boolean(timeHour) && Boolean(timeMinute);

  const collectionScheduleInPast = useMemo(
    () =>
      scheduleComplete &&
      isUkCollectionScheduleInPast(collectionDate, timeHour, timeMinute),
    [collectionDate, timeHour, timeMinute, scheduleComplete]
  );

  const setTimeHourMinute = useCallback(({ hour, minute }) => {
    setTimeHour(hour);
    setTimeMinute(minute);
  }, []);

  const reset = useCallback(() => {
    if (hasAppliedDefaults) {
      const defaults = getUkPrebookTimeReadyDefaultsOneHourFromNow();
      setCollectionDate(todayIso);
      setTimeHour(defaults.hour);
      setTimeMinute(defaults.minute);
      return;
    }
    setCollectionDate('');
    setTimeHour('');
    setTimeMinute('');
  }, [hasAppliedDefaults, todayIso]);

  return {
    collectionDate,
    timeHour,
    timeMinute,
    todayIso,
    collectionDateMaxIso,
    scheduleComplete,
    collectionScheduleInPast,
    setCollectionDate,
    setTimeHourMinute,
    reset,
  };
}
